import React, { useEffect, useState } from "react";
import { CheckCircle2, Package, Truck, Plane, Bell } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";
import WORLD_DOTS from "../data/world_dots.json";

/**
 * GlobeNetworkScene — Professional enterprise-grade logistics hero.
 *
 *  - Dotted world map (1700+ dots traced from real continent geometry)
 *  - Pulsing red logistics hubs (Muscat = origin, plus 8 partner cities)
 *  - Animated arcs draw out of Muscat in a continuous, seamless loop
 *  - Parcels travel along arcs with motion trails
 *  - Subtle horizontal scan sweep adds a "live network" feel
 *  - Notification feed on the right is preserved (unchanged contract)
 */

const NOTIF_ICONS = [Package, Truck, Plane, CheckCircle2];
const NOTIF_TONES = ["red", "navy", "navy", "success"];

// ─── Hubs (in the 1000×500 viewBox of the world dot map) ───────────────
const HUBS = [
  { id: "muscat",   x: 645, y: 245, label: "MUSCAT",    primary: true  },
  { id: "dubai",    x: 635, y: 232, label: "DUBAI",     primary: false },
  { id: "riyadh",   x: 605, y: 240, label: "RIYADH",    primary: false },
  { id: "london",   x: 488, y: 158, label: "LONDON",    primary: false },
  { id: "newyork",  x: 305, y: 200, label: "NEW YORK",  primary: false },
  { id: "mumbai",   x: 700, y: 268, label: "MUMBAI",    primary: false },
  { id: "singapore",x: 790, y: 305, label: "SINGAPORE", primary: false },
  { id: "shanghai", x: 830, y: 220, label: "SHANGHAI",  primary: false },
  { id: "nairobi",  x: 595, y: 320, label: "NAIROBI",   primary: false },
];

// Each route originates from Muscat and is offset in the loop cycle
const ROUTE_TARGETS = ["london", "shanghai", "newyork", "singapore", "mumbai", "nairobi", "dubai"];
const LOOP_DURATION = 9; // seconds — total seamless loop length
const ROUTES = ROUTE_TARGETS.map((to, i) => ({
  from: "muscat",
  to,
  delay: -(i * (LOOP_DURATION / ROUTE_TARGETS.length)),
}));

const byId = (id) => HUBS.find((h) => h.id === id);

// Quadratic-bezier arc between two hubs with a high control point
function arcPath(a, b) {
  const mx = (a.x + b.x) / 2;
  const dx = Math.abs(b.x - a.x);
  const lift = Math.min(140, 50 + dx * 0.22);
  const my = Math.min(a.y, b.y) - lift;
  return `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`;
}

export default function GlobeNetworkScene() {
  const t = useT();
  const NOTIFS = (t.hero.notifications || []).map((n, i) => ({
    icon: NOTIF_ICONS[i],
    title: n.title,
    meta: n.meta,
    tone: NOTIF_TONES[i],
  }));
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActiveStep((prev) => (prev >= NOTIFS.length - 1 ? 0 : prev + 1));
    }, 2500);
    return () => clearInterval(id);
  }, [NOTIFS.length]);

  return (
    <div className="relative w-full" data-testid="hero-animated-delivery">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8 items-stretch">
        {/* ===== Globe network scene ===== */}
        <div
          className="relative rounded-3xl overflow-hidden border border-white/10 globe-scene"
          style={{
            background:
              "radial-gradient(120% 80% at 50% 0%, #2E2E2E 0%, #1A1A1A 55%, #0F0F0F 100%)",
          }}
          data-testid="hero-globe-scene"
        >
          {/* Faint starfield */}
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            {Array.from({ length: 28 }).map((_, i) => (
              <span
                key={i}
                className="absolute rounded-full bg-white star-twinkle"
                style={{
                  width: i % 6 === 0 ? 2 : 1,
                  height: i % 6 === 0 ? 2 : 1,
                  left: `${(i * 31) % 100}%`,
                  top: `${(i * 17) % 60}%`,
                  opacity: 0.18 + ((i * 7) % 10) / 40,
                  animationDelay: `${(i * 0.4) % 3}s`,
                  boxShadow: "0 0 4px rgba(255,255,255,0.6)",
                }}
              />
            ))}
          </div>

          {/* Soft red glow behind Muscat */}
          <div
            className="absolute pointer-events-none"
            style={{
              left: "58%",
              top: "30%",
              width: "44%",
              height: "60%",
              background:
                "radial-gradient(closest-side, rgba(152,27,55,0.22), rgba(152,27,55,0) 70%)",
              filter: "blur(2px)",
            }}
            aria-hidden="true"
          />

          {/* World dot map + network arcs */}
          <svg
            viewBox="0 0 1000 500"
            className="absolute inset-0 w-full h-full"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="routeGrad" x1="0" x2="1">
                <stop offset="0%"   stopColor="#981B37" stopOpacity="0" />
                <stop offset="50%"  stopColor="#981B37" stopOpacity="1" />
                <stop offset="100%" stopColor="#FFB1B8" stopOpacity="1" />
              </linearGradient>
              <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2.4" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="rgba(152,27,55,0.55)" />
                <stop offset="100%" stopColor="rgba(152,27,55,0)" />
              </radialGradient>
            </defs>

            {/* Latitude / longitude faint grid */}
            <g stroke="rgba(255,255,255,0.05)" strokeWidth="0.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <line key={`lat-${i}`} x1="0" x2="1000" y1={70 + i * 70} y2={70 + i * 70} />
              ))}
              {Array.from({ length: 11 }).map((_, i) => (
                <line key={`lon-${i}`} x1={i * 100} x2={i * 100} y1="0" y2="500" />
              ))}
            </g>

            {/* WORLD DOTS — the recognisable dotted map */}
            <g className="world-dots">
              {WORLD_DOTS.map(([x, y], i) => (
                <circle
                  key={i}
                  cx={x}
                  cy={y}
                  r="1.6"
                  fill="rgba(220,232,255,0.78)"
                />
              ))}
            </g>

            {/* Connection arcs from Muscat */}
            {ROUTES.map((r, i) => {
              const a = byId(r.from);
              const b = byId(r.to);
              if (!a || !b) return null;
              const d = arcPath(a, b);
              return (
                <g key={`r-${i}`}>
                  {/* dim base */}
                  <path
                    d={d}
                    fill="none"
                    stroke="rgba(152,27,55,0.18)"
                    strokeWidth="0.8"
                    strokeDasharray="2 5"
                  />
                  {/* animated bright stroke */}
                  <path
                    d={d}
                    fill="none"
                    stroke="url(#routeGrad)"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    className="route-trace"
                    style={{
                      animationDelay: `${r.delay}s`,
                      animationDuration: `${LOOP_DURATION}s`,
                    }}
                    filter="url(#softGlow)"
                  />
                  {/* parcel dot riding the route */}
                  <circle
                    r="2.6"
                    fill="#fff"
                    className="route-dot"
                    style={{
                      offsetPath: `path("${d}")`,
                      animationDelay: `${r.delay}s`,
                      animationDuration: `${LOOP_DURATION}s`,
                      filter: "drop-shadow(0 0 6px rgba(152,27,55,0.95))",
                    }}
                  />
                </g>
              );
            })}

            {/* Hubs — pulsing nodes */}
            {HUBS.map((h) => (
              <g key={h.id}>
                <circle cx={h.x} cy={h.y} r={h.primary ? 18 : 11} fill="url(#hubGlow)" />
                <circle
                  cx={h.x}
                  cy={h.y}
                  r={h.primary ? 10 : 6}
                  fill="rgba(152,27,55,0.18)"
                  className="hub-pulse"
                  style={{ transformBox: "fill-box", transformOrigin: "center" }}
                />
                <circle cx={h.x} cy={h.y} r={h.primary ? 4 : 2.4} fill="#981B37" />
                <circle cx={h.x} cy={h.y} r={h.primary ? 1.6 : 1} fill="#FFFFFF" />
                {h.primary && (
                  <text
                    x={h.x + 12}
                    y={h.y + 4}
                    fill="rgba(255,255,255,0.92)"
                    fontSize="11"
                    fontWeight="800"
                    letterSpacing="0.2em"
                    style={{ paintOrder: "stroke", stroke: "rgba(15,15,15,0.7)", strokeWidth: 2 }}
                  >
                    {h.label}
                  </text>
                )}
              </g>
            ))}

            {/* Subtle scan-line sweeping across the map */}
            <rect
              x="-200"
              y="0"
              width="200"
              height="500"
              fill="url(#scanFill)"
              className="scan-sweep"
            />
            <defs>
              <linearGradient id="scanFill" x1="0" x2="1">
                <stop offset="0%"  stopColor="rgba(152,27,55,0)" />
                <stop offset="50%" stopColor="rgba(152,27,55,0.08)" />
                <stop offset="100%" stopColor="rgba(152,27,55,0)" />
              </linearGradient>
            </defs>
          </svg>

          {/* Top-left status pill */}
          <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur text-[11px] uppercase tracking-[0.18em] text-white/80 z-10">
            <span className="relative flex w-2 h-2">
              <span className="absolute inline-flex w-full h-full rounded-full bg-delfi-red opacity-75 animate-ping" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-delfi-red" />
            </span>
            {t.hero.sceneLabel}
          </div>

          {/* Top-right meta */}
          <div className="absolute top-4 right-4 flex items-center gap-3 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur text-[11px] uppercase tracking-[0.18em] text-white/70 z-10">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-delfi-red" /> {t.hero.activeBadge}
            </span>
            <span className="w-px h-3 bg-white/15" />
            <span>{t.hero.zoneBadge}</span>
          </div>

          {/* Mode legend */}
          <div className="absolute bottom-4 left-4 flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.18em] text-white/65 z-10">
            <span className="px-2 py-1 rounded-md bg-white/5 border border-white/10 flex items-center gap-1.5">
              <Plane size={11} className="text-delfi-red" /> International
            </span>
            <span className="px-2 py-1 rounded-md bg-white/5 border border-white/10 flex items-center gap-1.5 hidden sm:inline-flex">
              <Truck size={11} className="text-delfi-red" /> Cargo
            </span>
            <span className="px-2 py-1 rounded-md bg-white/5 border border-white/10 flex items-center gap-1.5 hidden sm:inline-flex">
              <Package size={11} className="text-delfi-red" /> Last-mile
            </span>
          </div>

          {/* Aspect spacer */}
          <div style={{ paddingTop: "62%" }} aria-hidden="true" />
        </div>

        {/* ===== Notification feed (unchanged design) ===== */}
        <div
          className="relative rounded-3xl p-5 md:p-6 bg-white/[0.035] border border-white/10 backdrop-blur-sm flex flex-col"
          data-testid="hero-notification-feed"
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Bell size={14} className="text-delfi-red" />
              <span className="text-[11px] uppercase tracking-[0.2em] text-white/70 font-semibold">
                {t.hero.notifTitle}
              </span>
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-white/40">
              {t.hero.notifJustNow}
            </span>
          </div>

          <div className="flex flex-col gap-3 flex-1">
            {NOTIFS.map((n, i) => {
              const isActive = i <= activeStep;
              const isCurrent = i === activeStep;
              const Icon = n.icon;
              const bgTone =
                n.tone === "red"
                  ? "bg-delfi-red/15 text-delfi-red border-delfi-red/30"
                  : n.tone === "success"
                  ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/25"
                  : "bg-white/8 text-white border-white/15";
              return (
                <div
                  key={n.title}
                  className={`flex items-start gap-3 p-3.5 rounded-xl border transition-all duration-500 ${
                    isActive
                      ? `${bgTone} opacity-100 translate-y-0`
                      : "bg-white/[0.02] border-white/5 opacity-40 translate-y-1"
                  } ${isCurrent ? "ring-1 ring-delfi-red/50 shadow-[0_10px_30px_-12px_rgba(152,27,55,0.6)]" : ""}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isActive ? "bg-white/10" : "bg-white/[0.04]"}`}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{n.title}</p>
                    <p className="text-[11px] text-white/55 mt-0.5 truncate">{n.meta}</p>
                  </div>
                  {isCurrent && (
                    <span className="text-[10px] uppercase tracking-wider text-delfi-red font-bold animate-pulse">
                      Live
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-5 h-1 rounded-full bg-white/8 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-delfi-red to-[#ff6b7a] transition-[width] duration-700 ease-out"
              style={{ width: `${((activeStep + 1) / NOTIFS.length) * 100}%` }}
            />
          </div>
          <p className="mt-3 text-[11px] text-white/50 flex items-center justify-between">
            <span>{t.hero.notifEtaUpdates}</span>
            <span className="text-white/70 font-semibold">
              {activeStep + 1}/{NOTIFS.length}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
